import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const values = [
  {
    icon: "ri-moon-clear-line",
    title: "专注午夜时段",
    description: "我们只推荐适合深夜观看的影片，从氛围而非热度出发，帮你找到此刻真正想看的作品。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "完整的影片资料",
    description: "每部影片都附带导演、主演、类型、片长与剧情梗概，并附上具体的午夜观影建议。",
  },
  {
    icon: "ri-compass-3-line",
    title: "按情绪找片",
    description: "除了类型与地区，我们更鼓励你根据当下情绪选片，让观影体验更贴近真实需求。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@midnightcinema.demo" },
  { icon: "ri-weibo-line", label: "微博", value: "@午夜场的电影" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "midnight-cinema" },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 午夜场的电影",
    description:
      "了解午夜场的电影的定位与愿景：一个专注于午夜档电影推荐与影视资讯的内容平台，为夜间观影爱好者提供选片与观影建议。",
    keywords: "关于午夜场的电影,午夜电影平台,午夜电影推荐网站",
  });

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6 lg:px-10">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
            <Link to="/" className="transition-colors hover:text-primary-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-300">关于我们</span>
          </nav>
          <span className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-400">
            <span className="h-1 w-6 rounded-full bg-primary-500" />
            About Us
          </span>
          <h1 className="mb-4 max-w-3xl font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-50 md:text-5xl">
            为醒着的人，做一份真诚的午夜片单
          </h1>
          <p className="max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">
            “午夜场的电影”是一个专注于午夜档电影推荐与影视资讯的平台。
            我们相信，深夜的观影体验与白天截然不同，因此需要的不是“最热门”，
            而是“最适合此刻”的那一部。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="mb-4 font-heading text-2xl font-extrabold text-foreground-50">
              我们的定位与愿景
            </h2>
            <div className="space-y-4 text-sm leading-loose text-foreground-400">
              <p>
                我们面向夜间观影爱好者、电影迷，以及所有在深夜寻找一部好片的人。
                通过类型、地区、年份的多维归档，加上清晰的影片资料与观看建议，
                我们希望把“选择看什么”这件事变得简单而愉快。
              </p>
              <p>
                我们的愿景，是成为最懂午夜时段的影视内容平台：
                不只是罗列片单，而是理解你在凌晨两点真正需要的是什么——
                是被吓一跳的刺激，是被理解的温柔，还是被推一把的勇气。
              </p>
              <p>
                目前站内的影片、资讯与推荐均为演示数据，用于展示完整的浏览、筛选、搜索与收藏体验；
                正式上线时，这些内容可以直接替换为真实的片库与稿件数据。
              </p>
            </div>
          </div>

          <aside className="rounded-lg border border-background-200/80 bg-background-100 p-6">
            <h3 className="mb-4 font-heading text-base font-bold text-foreground-50">联系方式</h3>
            <ul className="space-y-4">
              {contacts.map((contact) => (
                <li key={contact.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-900/50 text-secondary-200">
                    <i className={`${contact.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">{contact.label}</p>
                    <p className="text-sm text-foreground-200">{contact.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((value) => (
            <div
              key={value.title}
              className="rounded-lg border border-background-200/80 bg-background-100 p-6"
            >
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                <i className={`${value.icon} text-xl`}></i>
              </span>
              <h3 className="mb-2 font-heading text-base font-bold text-foreground-50">
                {value.title}
              </h3>
              <p className="text-sm leading-relaxed text-foreground-500">{value.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200/80 bg-background-100 p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
              <i className="ri-book-2-line text-primary-400"></i>
              使用说明
            </h2>
            <ol className="space-y-3 text-sm leading-relaxed text-foreground-400">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-200/70 text-xs font-bold text-primary-400">
                  1
                </span>
                在首页浏览午夜精选，或通过电影分类页按类型、地区、年份筛选影片。
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-200/70 text-xs font-bold text-primary-400">
                  2
                </span>
                点击影片卡片进入详情页，查看完整资料、剧情梗概与午夜观影建议。
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-200/70 text-xs font-bold text-primary-400">
                  3
                </span>
                使用顶部搜索框检索影片或资讯，收藏喜欢的作品会保存在你的浏览器本地。
              </li>
            </ol>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-100 p-6">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
              <i className="ri-shield-check-line text-primary-400"></i>
              免责声明
            </h2>
            <div className="space-y-3 text-sm leading-relaxed text-foreground-400">
              <p>
                本站当前所有影片、评分、资讯与图片均为演示用途的示例数据，不代表真实作品信息，
                也不构成任何观看引导或版权主张。
              </p>
              <p>
                收藏功能仅在本地浏览器保存，不会上传至服务器；清除浏览器数据后收藏记录将被移除，
                请知悉。
              </p>
              <p>
                如正式接入真实内容，请确保相关素材的授权合规，
                本站对演示数据可能造成的误解不承担法律责任。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
          >
            浏览电影分类
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}